import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
} from '@simplewebauthn/browser';
import type { LoginResponse } from '@/shared/api/types';
import { webauthnApi } from '../api/webauthn';
import {
  isUserCancelledWebauthnError,
  isWebauthnRegisteredOnThisDevice,
} from './webauthnLocalState';

interface UseWebAuthnLoginResult {
  /** Whether the "Войти по Face ID" affordance should render at all. */
  available: boolean;
  /** True while a ceremony (auto-triggered or manual) is in flight. */
  busy: boolean;
  error: string | null;
  login: () => void;
}

/* Auto-triggering a biometric prompt is only safe once we've confirmed a platform authenticator
   actually exists — firing it eagerly and finding out via the ceremony's own failure is the race
   nafta hit (a doomed attempt could start while the availability check was still in flight). We
   gate `available` on that confirmed check, and only auto-fire once it flips true. */
export function useWebAuthnLogin(onSuccess: (resp: LoginResponse) => void): UseWebAuthnLoginResult {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoTriggered = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!browserSupportsWebAuthn() || !isWebauthnRegisteredOnThisDevice()) return;
    let cancelled = false;
    void platformAuthenticatorIsAvailable()
      .catch(() => false)
      .then((ok) => {
        if (!cancelled && ok) setAvailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(() => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const { challenge_id, options } = await webauthnApi.loginOptions();
        const credential = await startAuthentication({ optionsJSON: options });
        const resp = await webauthnApi.login(challenge_id, credential);
        onSuccessRef.current(resp);
      } catch (err) {
        // A user-dismissed OS prompt is not a real failure — leave the password form as the
        // silent fallback instead of scaring the user with an error they didn't cause.
        if (!isUserCancelledWebauthnError(err))
          setError('Не удалось войти по Face ID. Попробуйте пароль.');
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!available || autoTriggered.current) return;
    autoTriggered.current = true;
    login();
  }, [available, login]);

  return { available, busy, error, login };
}
